// Phase 10B — Inbound Calls page restyled in the AutoMind 2050 design language.
// Logic, polling, and data shape are preserved; only JSX + classNames change.
// No mock data fallbacks: API errors render an EmptyState.

import React, { useEffect, useMemo, useState } from 'react';
import { PhoneIncoming, RefreshCw } from 'lucide-react';
import { PageHeader } from '../components/ui/page-header';
import { SectionGrid } from '../components/ui/section-grid';
import { MetricCard } from '../components/ui/metric-card';
import { DataCard } from '../components/ui/data-card';
import { StatusPill } from '../components/ui/status-pill';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';

// Map call status -> StatusPill tone.
const statusTone = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'in-progress':
    case 'in_progress':
      return 'info';
    case 'ringing':
    case 'queued':
      return 'gold';
    case 'failed':
      return 'danger';
    case 'no-answer':
    case 'no_answer':
      return 'warning';
    default:
      return 'neutral';
  }
};

// Redact phone numbers to ***<last4>.
const redactPhone = (phone) => {
  if (!phone) return '—';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 4) return '***';
  return `***${digits.slice(-4)}`;
};

const formatDuration = (ms) => {
  if (!ms) return '—';
  const seconds = Math.round(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const InboundCalls = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    fetchInboundCalls();

    // Auto-refresh every 30 seconds
    const intervalId = setInterval(() => {
      fetchInboundCalls(false);
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const fetchInboundCalls = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);
      // Phase Audit Fix C follow-up: bare fetch with no Authorization
      // header was 401'ing silently and showing an empty page. Route
      // through callsApi/apiFetch so the JWT goes along with the
      // request. Same bug pattern fixed earlier in AI Agents.
      const { callsApi } = await import('../api');
      const data = await callsApi.inbound();
      setCalls(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      console.error('Error fetching inbound calls:', error);
      setLoadError(true);
      if (showLoading) {
        toast({
          title: 'Error',
          description: 'Failed to fetch inbound calls',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Stats — derived from real `calls` data only. No random/mock fallbacks.
  const stats = useMemo(() => {
    const inQueue = calls.filter((c) =>
      ['ringing', 'queued'].includes(c.status)
    ).length;
    const inProgress = calls.filter((c) =>
      ['in-progress', 'in_progress'].includes(c.status)
    ).length;
    const connected = calls.filter((c) => c.status === 'completed').length;
    const missed = calls.filter((c) =>
      ['failed', 'no-answer', 'no_answer'].includes(c.status)
    ).length;
    return { inQueue, inProgress, connected, missed };
  }, [calls]);

  const filteredCalls = useMemo(() => {
    if (!searchQuery) return calls;
    return calls.filter(
      (call) =>
        call.phone_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        call.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [calls, searchQuery]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Calls"
        title="Inbound Calls"
        description="Live customer-initiated calls and queue."
        actions={
          <button
            type="button"
            className="btn-2050 btn-secondary"
            onClick={() => fetchInboundCalls(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Stats */}
      <div className="mt-6">
        <SectionGrid cols={4}>
          <MetricCard
            label="In queue"
            value={stats.inQueue}
            loading={loading && calls.length === 0}
            empty={loadError}
          />
          <MetricCard
            label="In progress"
            value={stats.inProgress}
            loading={loading && calls.length === 0}
            empty={loadError}
          />
          <MetricCard
            label="Connected"
            value={stats.connected}
            loading={loading && calls.length === 0}
            empty={loadError}
          />
          <MetricCard
            label="Missed"
            value={stats.missed}
            loading={loading && calls.length === 0}
            empty={loadError}
          />
        </SectionGrid>
      </div>

      {/* Search */}
      <div className="mt-6 max-w-sm">
        <Input
          placeholder="Search by phone or name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Calls table */}
      <div className="mt-6">
        <DataCard
          title="Inbound calls"
          description={
            loading
              ? 'Loading…'
              : `${filteredCalls.length} call${filteredCalls.length === 1 ? '' : 's'}`
          }
          loading={loading && calls.length === 0}
          empty={!loading && (loadError || filteredCalls.length === 0)}
          emptyTitle={loadError ? 'No data yet' : 'No inbound calls yet'}
          emptyDescription={
            loadError
              ? 'Unable to load right now.'
              : 'When customers call your inbound number, they will appear here.'
          }
          emptyIcon={PhoneIncoming}
          bodyClassName={
            !loading && (loadError || filteredCalls.length === 0) ? 'p-6' : 'p-0'
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-[0.16em] text-ink-500 bg-ink-50/60 dark:bg-ink-900/40">
                <tr>
                  <th className="px-6 py-3 text-left font-medium">Time</th>
                  <th className="px-6 py-3 text-left font-medium">Phone</th>
                  <th className="px-6 py-3 text-left font-medium">Status</th>
                  <th className="px-6 py-3 text-left font-medium">Duration</th>
                  <th className="px-6 py-3 text-left font-medium">Cost</th>
                  <th className="px-6 py-3 text-left font-medium">Disposition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200/60 dark:divide-ink-800/60">
                {filteredCalls.map((call) => (
                  <tr
                    key={call.id}
                    onClick={() => setSelectedCall(call)}
                    className="row-2050 hover:bg-ink-50/60 dark:hover:bg-ink-900/40 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-3 nums text-ink-500">
                      {formatDateTime(call.created_at)}
                    </td>
                    <td className="px-6 py-3 nums text-ink-700 dark:text-ink-200">
                      {redactPhone(call.phone_number)}
                    </td>
                    <td className="px-6 py-3">
                      <StatusPill
                        tone={statusTone(call.status)}
                        dot
                        label={String(call.status || 'unknown').replace('_', '-')}
                      />
                    </td>
                    <td className="px-6 py-3 nums text-ink-700 dark:text-ink-200">
                      {formatDuration(call.duration_ms)}
                    </td>
                    <td className="px-6 py-3 nums text-ink-700 dark:text-ink-200">
                      {call.cost != null ? `$${Number(call.cost).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-6 py-3 text-ink-500">
                      {call.disposition
                        ? String(call.disposition).replace(/_/g, ' ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataCard>
      </div>

      {/* Call Details Modal */}
      {selectedCall && (
        <div
          className="fixed inset-0 bg-ink-950/60 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={() => setSelectedCall(null)}
        >
          <div
            className="card-2050 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500">
                  Inbound call
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight mt-1">
                  {redactPhone(selectedCall.phone_number)}
                </h3>
              </div>
              <button
                type="button"
                className="btn-2050 btn-ghost"
                onClick={() => setSelectedCall(null)}
              >
                Close
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-ink-500 mb-1">Status</div>
                  <StatusPill
                    tone={statusTone(selectedCall.status)}
                    dot
                    label={String(selectedCall.status || 'unknown').replace('_', '-')}
                  />
                </div>
                <div>
                  <div className="text-xs text-ink-500 mb-1">Duration</div>
                  <div className="nums text-ink-900 dark:text-ink-50">
                    {formatDuration(selectedCall.duration_ms)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 mb-1">Cost</div>
                  <div className="nums text-ink-900 dark:text-ink-50">
                    {selectedCall.cost != null
                      ? `$${Number(selectedCall.cost).toFixed(2)}`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 mb-1">Quality score</div>
                  <div className="nums text-ink-900 dark:text-ink-50">
                    {selectedCall.call_quality_score ?? '—'}
                  </div>
                </div>
              </div>

              {selectedCall.transcript && (
                <div>
                  <div className="text-xs text-ink-500 mb-2">Transcript</div>
                  <div className="p-4 rounded-lg bg-ink-50 dark:bg-ink-900/60 text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">
                    {selectedCall.transcript}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboundCalls;
